import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { NgModule, importProvidersFrom } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  PreloadAllModules,
  RouterModule,
} from '@angular/router';
import {
  MissingTranslationHandler,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';

import { CurrencyPipe, DecimalPipe, PercentPipe } from '@angular/common';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
  MatRippleModule,
} from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_FORM_FIELD_DEFAULT_OPTIONS,
  MatFormFieldDefaultOptions,
} from '@angular/material/form-field';
import {
  MAT_SNACK_BAR_DEFAULT_OPTIONS,
  MatSnackBarConfig,
  MatSnackBarModule,
} from '@angular/material/snack-bar';
import {
  PROFILES_WIDGET_CONFIG_TOKEN,
  CommunityWidgetInfoUtilService,
  profilesWidgetConfigFactory,
} from '@trp/community/shared/community-widget';
import { CollapsibleMenuService } from '@trp/community/shared/data-access-collapsible-menu';
import {
  ContainersPipe,
  CurbsideContaminationPipe,
  PipesModule,
  CartSizesPipe,
  AdditionalServicesPipe,
  MethodsPipe,
} from '@trp/community/shared/ui';
import { BannerModule } from '@trp/community/ui/banner';
import { BaseLayoutModule } from '@trp/community/ui/base-layout';
import { ContentLayoutModule } from '@trp/community/ui/content-layout';
import { LoaderModule } from '@trp/community/ui/loader';
import {
  CommunityUserFeatureUserModule,
} from '@trp/community/user/feature-user';
import {
} from '@trp/deployment-check/ui-config';
import { ReportingClientModule } from '@trp/shared/reporting/client';
import {
  CustomMissingTranslationHandler,
  LoaderFactory,
} from '@trp/shared/services';
import * as EN from '../assets/i18n/en.json';
import { environment } from '../environments/environment';
import { ApiHttpInterceptor } from './api-http.interceptor';
import { AppComponent } from './app.component';
import { BannerContainerComponent } from './components/banner-container/banner-container.component';
import { NavComponent } from './components/nav/nav.component';
import { SidebarComponent } from './components/sidebar/sidebar.component';
import { TopbarComponent } from './components/topbar/topbar.component';
import { VerifyEmailBannerComponent } from './components/verify-email-banner/verify-email-banner.component';
import { MenuItemDirective } from './directives/menu-item.directive';
import { provideLoadUser } from './load-user.provider';
import { provideRecyclingCategories } from './recycling-categories.provider';
import { SharedUiSpinnerModule } from '@trp/shared/ui/spinner';
import { HUBSPOT_CONFIG } from '@trp/community/shared/ui';
import { routes } from './app.routes';
import { DateFnsAdapter, MAT_DATE_FNS_FORMATS } from '@angular/material-date-fns-adapter';
import { enUS } from 'date-fns/locale';
@NgModule({
  declarations: [
    AppComponent,
    SidebarComponent,
    NavComponent,
    BannerContainerComponent,
    VerifyEmailBannerComponent,
    TopbarComponent,
    MenuItemDirective,
  ],
  imports: [
    BannerModule,
    ContentLayoutModule,
    BaseLayoutModule,
    ReportingClientModule.forRoot(environment.reporting),
    BrowserModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    BrowserAnimationsModule,
    HttpClientModule,
    ContentLayoutModule,
    MatIconModule,
    MatSnackBarModule,
    MatRippleModule,
    SharedUiSpinnerModule,
    CommunityUserFeatureUserModule.forRoot({
      authApiUrl: environment.apiUrl + '/auth',
    }),
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: CustomMissingTranslationHandler,
      },
      loader: {
        provide: TranslateLoader,
        useValue: LoaderFactory({ en: EN }),
      },
    }),
    RouterModule.forRoot(
      routes,
      {
        initialNavigation: 'enabledBlocking',
        scrollPositionRestoration: 'enabled',
        preloadingStrategy: PreloadAllModules,
      }
    ),
    LoaderModule.forRoot({
      fetch: {
        get: false,
        post: false,
        delete: false,
        put: false,
      },
    }),
    // MatDateFnsModule
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: ApiHttpInterceptor, multi: true },
    {
      provide: DateAdapter,
      useClass: DateFnsAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_LOCALE, useValue: enUS },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },    
    provideLoadUser(),
    provideRecyclingCategories(),
    {
      provide: MAT_SNACK_BAR_DEFAULT_OPTIONS,
      useValue: {
        duration: 3000,
        verticalPosition: 'top',
      } as MatSnackBarConfig<void>,
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'outline',
        floatLabel: 'always',
      } as MatFormFieldDefaultOptions,
    },
    { provide: HUBSPOT_CONFIG, useValue: environment.hubspot },
    CollapsibleMenuService,
    CommunityWidgetInfoUtilService,
    importProvidersFrom(PipesModule),
    {
      provide: PROFILES_WIDGET_CONFIG_TOKEN,
      useFactory: profilesWidgetConfigFactory,
      deps: [
        TranslateService,
        CommunityWidgetInfoUtilService,
        ContainersPipe,
        DecimalPipe,
        CurrencyPipe,
        PercentPipe,
        CurbsideContaminationPipe,
        CartSizesPipe,
        AdditionalServicesPipe,
        MethodsPipe,
      ],
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
